import React, { useState } from 'react';
import './InteractPanel.css';

/**
 * 互动面板组件 - 多Tab版本
 * @param {Object} props
 * @param {boolean} props.isOpen - 面板是否打开
 * @param {Function} props.onClose - 关闭回调
 * @param {Array} props.videos - 视频列表
 * @param {Function} props.handleInteraction - 互动处理函数
 * @param {Function} props.getStreamStatus - 获取直播状态函数
 * @param {string} props.activeButton - 当前激活的按钮
 * @param {number} props.streamId - 直播间ID
 * @param {Function} props.onIntimateGenerationStart - 开始生成亲密互动的回调
 */
const InteractPanel = ({ 
  isOpen, 
  onClose, 
  videos, 
  handleInteraction, 
  getStreamStatus,
  activeButton,
  streamId,
  onIntimateGenerationStart
}) => {
  const [activeTab, setActiveTab] = useState('popular'); // 'popular' | 'personal' | 'create'
  
  // 阻止面板内部点击事件冒泡，避免关闭面板
  const handlePanelClick = (e) => {
    e.stopPropagation();
  };

  const isIntroOrIdle = (v) => v.video_category === 'intro' || v.video_category === 'idle';

  // 大家都在玩：排除片头、待机、私人
  const publicVideos = videos.filter(v => !isIntroOrIdle(v) && v.video_category !== 'private');
  const privateVideos = videos.filter(v => v.video_category === 'private');

  return (
    <div 
      className={`interact-panel-popup ${isOpen ? 'open' : ''}`}
      onClick={handlePanelClick}
    >
      {/* Tab 切换栏 - 三个Tab */}
      <div className="interact-tabs">
        <button
          className={`interact-tab ${activeTab === 'popular' ? 'active' : ''}`}
          onClick={() => setActiveTab('popular')}
        >
          <span className="tab-icon">🌟</span>
          <span className="tab-label">大家都在玩</span>
        </button>
        <button
          className={`interact-tab ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          <span className="tab-icon">💫</span>
          <span className="tab-label">私人定制</span>
        </button>
        <button
          className={`interact-tab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          <span className="tab-icon">✨</span>
          <span className="tab-label">整活时间</span>
        </button>
        <button className="interact-panel-close" onClick={onClose}>×</button>
      </div>
      
      <div className="interact-panel-content">
        {activeTab === 'popular' && (
          // 第一个Tab：大家都在玩（公开动作）
          <ActionsTab 
            videos={publicVideos}
            handleInteraction={handleInteraction}
            getStreamStatus={getStreamStatus}
            activeButton={activeButton}
            onClose={onClose}
            emptyIcon="📹"
            emptyText="主角还没设置互动动作"
            emptyHint="敬请期待～"
          />
        )}
        {activeTab === 'personal' && (
          // 第二个Tab：私人定制（个人动作）
          <ActionsTab 
            videos={privateVideos}
            handleInteraction={handleInteraction}
            getStreamStatus={getStreamStatus}
            activeButton={activeButton}
            onClose={onClose}
            emptyIcon="🎨"
            emptyText="还没有你的专属动作"
            emptyHint="去「整活时间」创建一个吧！"
            showCreateButton={true}
            onCreateClick={() => setActiveTab('create')}
          />
        )}
        {activeTab === 'create' && (
          // 第三个Tab：整活时间（创作互动）
          <IntimateTab 
            streamId={streamId}
            onGenerationStart={(taskId) => {
              // 通知父组件开始生成，传递 task_id
              if (onIntimateGenerationStart) {
                onIntimateGenerationStart(taskId);
              }
              // 关闭面板
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * 动作展示 Tab（用于"大家都在玩"和"私人定制"）
 */
const ActionsTab = ({ 
  videos, 
  handleInteraction, 
  getStreamStatus, 
  activeButton, 
  onClose,
  emptyIcon = '📹',
  emptyText = '暂无互动内容',
  emptyHint = '',
  showCreateButton = false,
  onCreateClick
}) => {
  const streamStatus = getStreamStatus();
  
  // 如果是休息状态，显示休息提示
  if (streamStatus.isResting) {
    return (
      <div className="interact-resting-mode">
        <div className="interact-resting-icon">😴</div>
        <div className="interact-resting-text">主角正在休息中</div>
        <div className="interact-resting-subtext">暂时无法互动，请稍后再来</div>
      </div>
    );
  }
  
  // 获取可用的互动动作
  const specialInteractions = videos
    .filter(video => video.interaction_type && video.interaction_label)
    .map(video => ({
      id: video.id,
      type: video.interaction_type,
      label: video.interaction_label,
      emoji: video.emoji || '🎭',
      isCandidate: false
    }));
  
  if (specialInteractions.length > 0) {
    return (
      <div className="interact-actions-grid">
        {specialInteractions.map((interaction) => (
          <button
            key={interaction.id}
            className={`interact-action-btn ${activeButton === interaction.type ? 'active' : ''}`}
            onClick={() => {
              handleInteraction(interaction.id);
              onClose();
            }}
            title={`点击${interaction.label}`}
          >
            <span className="interact-btn-emoji">{interaction.emoji}</span>
            <span className="interact-btn-label">{interaction.label}</span>
          </button>
        ))}
      </div>
    );
  } else {
    return (
      <div className="interact-no-content">
        <div className="interact-no-icon">{emptyIcon}</div>
        <div className="interact-no-text">{emptyText}</div>
        {emptyHint && <div className="interact-no-hint">{emptyHint}</div>}
        {showCreateButton && (
          <button className="interact-go-create-btn" onClick={onCreateClick}>
            <span>✨</span>
            <span>去整活</span>
          </button>
        )}
      </div>
    );
  }
};

/**
 * 亲密互动 Tab
 */
const IntimateTab = ({ streamId, onGenerationStart }) => {
  const [interactionPrompt, setInteractionPrompt] = useState('');
  const [characterImage, setCharacterImage] = useState(null);
  const [characterPreview, setCharacterPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showGestureHint, setShowGestureHint] = useState(false);
  
  // 处理文件选择
  const handleCharacterImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCharacterImage(file);
      
      // 生成预览图
      const reader = new FileReader();
      reader.onloadend = () => {
        setCharacterPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // 移除图片
  const handleRemoveImage = () => {
    setCharacterImage(null);
    setCharacterPreview(null);
  };
  
  // 提交生成请求
  const handleSubmit = async () => {
    if (!interactionPrompt.trim()) {
      setError('请输入互动动作描述');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('interaction_prompt', interactionPrompt);
      
      if (characterImage) {
        formData.append('character_image', characterImage);
      }
      
      // 发起生成请求
      const response = await fetch(`/streams/${streamId}/add-interaction`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '创建任务失败');
      }
      
      const result = await response.json();
      
      // 重置表单状态
      setIsSubmitting(false);
      setInteractionPrompt('');
      setCharacterImage(null);
      setCharacterPreview(null);
      
      // 通知父组件开始生成，并传递 task_id
      if (onGenerationStart) {
        onGenerationStart(result.task_id);
      }
      
    } catch (err) {
      console.error('创建实时互动失败:', err);
      setError(err.message || '创建失败，请重试');
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="intimate-tab-content">
      {/* 左右分栏布局 */}
      <div className="intimate-layout">
        {/* 左侧：动作描述 + 手势按钮 + 生成按钮 */}
        <div className="intimate-left">
          {/* 上：动作描述框 */}
          <textarea
            placeholder="✨ 互动动作描述&#10;例如：做一个爱心手势..."
            value={interactionPrompt}
            onChange={(e) => setInteractionPrompt(e.target.value)}
            className="intimate-textarea"
            disabled={isSubmitting}
          />
          
          {error && (
            <div className="intimate-error">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}
          
          {/* 中：手势交互按钮/说明 */}
          <div 
            className={`intimate-gesture-area ${showGestureHint ? 'expanded' : ''}`}
            onClick={() => setShowGestureHint(!showGestureHint)}
          >
            {showGestureHint ? (
              <span className="gesture-hint-text">
                💡 （开发中）手势与我互动，包括双指放大缩小、点击、画轨迹等方式
              </span>
            ) : (
              <>
                <span className="btn-icon">👆</span>
                <span className="btn-text">手势交互</span>
              </>
            )}
          </div>
          
          {/* 下：开始生成按钮 */}
          <button 
            className="intimate-submit-btn" 
            onClick={handleSubmit}
            disabled={!interactionPrompt.trim() || isSubmitting}
          >
            <span className="btn-icon">🎬</span>
            <span className="btn-text">{isSubmitting ? '提交中...' : '开始生成'}</span>
          </button>
        </div>
        
        {/* 右侧：图片上传（竖版） */}
        <div className="intimate-right">
          {!characterPreview ? (
            <div className="image-upload-area-vertical">
              <input
                type="file"
                accept="image/*"
                onChange={handleCharacterImageChange}
                id="character-image-upload"
                className="image-upload-input"
                disabled={isSubmitting}
              />
              <label htmlFor="character-image-upload" className="image-upload-label-vertical">
                <div className="upload-icon">📷</div>
                <div className="upload-text">点击上传你自己的照片</div>
                <div className="upload-hint">让主角跟你互动吧</div>
              </label>
            </div>
          ) : (
            <div className="image-preview-area-vertical">
              <img src={characterPreview} alt="预览" className="image-preview-vertical" />
              <button className="image-remove-btn" onClick={handleRemoveImage} disabled={isSubmitting}>
                <span>×</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InteractPanel;

